import { Button } from '@heidi/design-system/button';
import { useHorizontalScrollGradient } from '@heidi/design-system/hooks/use-horizontal-scroll-gradient';
import { cn } from '@/lib/cn';
import * as m from 'motion/react-m';
import { useRef, type ReactNode } from 'react';
import type { EvidenceVerbChipsProps } from './evidence-verb-suggestions.types';

/** Same 12px gradient the transcript fades use, turned onto the inline axis, on the card's `bg-surface`. */
const FADE_BASE =
  'pointer-events-none absolute inset-y-0 w-6 from-surface to-surface/0 transition-opacity duration-200';

const chipsMotion = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 8 },
  transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] },
} as const;

/**
 * One row, scrolled rather than wrapped. The scrollbar is hidden because the
 * fades are the affordance; wheel, trackpad, touch and keyboard focus still
 * scroll it. Its own component so the scroller's ref exists when the gradient
 * hook first measures.
 */
const ScrollingChipRow = ({ children }: { children: ReactNode }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { showLeftGradient, showRightGradient, handleScroll } = useHorizontalScrollGradient(
    scrollerRef,
    { observeContentChanges: true }
  );

  return (
    <m.div {...chipsMotion} className="relative w-full min-w-0">
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="scrollbar-none flex w-full items-center justify-start gap-2 overflow-x-auto"
        data-testid="evidence-verb-chips-row"
      >
        {children}
      </div>
      {/* The hook reports PHYSICAL edges (RTL-aware), so the fades sit on physical sides. */}
      <div
        aria-hidden="true"
        data-testid="evidence-verb-chips-fade-left"
        className={cn(
          FADE_BASE,
          'left-0 bg-gradient-to-r',
          showLeftGradient ? 'opacity-100' : 'opacity-0'
        )}
      />
      <div
        aria-hidden="true"
        data-testid="evidence-verb-chips-fade-right"
        className={cn(
          FADE_BASE,
          'right-0 bg-gradient-to-l',
          showRightGradient ? 'opacity-100' : 'opacity-0'
        )}
      />
    </m.div>
  );
};

export const EvidenceVerbChips = ({
  verbs,
  onVerbClick,
  isMobile = false,
  selectedId,
  align = 'center',
  overflow = 'wrap',
}: EvidenceVerbChipsProps) => {
  const isScrollRow = overflow === 'scroll' && !isMobile;

  const chips = verbs.map((verb) => {
    const Icon = verb.icon;
    const isSelected = selectedId === verb.id;
    return (
      <Button
        key={verb.id}
        variant={
          selectedId != null
            ? isSelected
              ? 'secondary'
              : 'ghost'
            : isMobile
              ? 'ghost'
              : 'outline'
        }
        size={isMobile ? 'md' : 'sm'}
        {...(isMobile && { flush: 'start' })}
        aria-pressed={selectedId == null ? undefined : isSelected}
        onClick={() => onVerbClick(verb.id)}
        className={cn('gap-2 text-text-secondary', isScrollRow && 'shrink-0')}
      >
        <Icon size={isMobile ? 16 : 14} />
        {verb.label}
      </Button>
    );
  });

  if (isScrollRow) return <ScrollingChipRow>{chips}</ScrollingChipRow>;

  return (
    <m.div
      {...chipsMotion}
      className={cn(
        'flex gap-2',
        isMobile
          ? 'flex-col items-start'
          : cn(
              'flex-wrap items-center',
              align === 'start' ? 'justify-start' : 'justify-center'
            )
      )}
    >
      {chips}
    </m.div>
  );
};
